import { useQuery } from "@hooks/useQuery";
import { TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { eramanCMSApi } from "~/Services";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn } from "~/Utils/TableRenderFunction";
import { useCategoriesDocumentDetails } from "./Categories.hook";

export const Categories = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.tid);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");

  const { data: drilldown, refetch: refetchDocument } = useCategoriesDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    const filteredData = data.filter((item: any) => {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      return decodeHTML(item.field_category_name).toLowerCase().includes(searchLower);
    });
    setSortedData(filteredData);
  }, [data, searchValue]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `/api/get-categories`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
          setOrderByName("field_category_name");
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    // {
    //   title: "Order Sequence",
    //   dataIndex: "field_category_order_sequence",
    //   key: "field_category_order_sequence",
    // },

    {
      title: "Name",
      dataIndex: "field_category_name",
      key: "field_category_name",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_category_name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_category_name"
            ? "descend"
            : undefined,
    },

    // ❌ Commented Published
    // {
    //   title: "Published",
    //   dataIndex: "field_category_published",
    //   key: "field_category_published",
    //   render: renderPublishedColumn,
    //   sorter: true,
    //   sortOrder: ...
    // },

    // ❌ Commented Created At
    // {
    //   title: "Created At",
    //   dataIndex: "field_category_created_at",
    //   key: "field_category_created_at",
    //   render: (value: any) => renderDateColumn(value),
    //   sorter: true,
    //   sortOrder: ...
    // },

    {
      title: "Updated At",
      dataIndex: "field_category_updated_at",
      key: "field_category_updated_at",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_category_updated_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_category_updated_at"
            ? "descend"
            : undefined,
    },

    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        return renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/product-management/categories/add?editId=${record.tid}`);
          },
          undefined
          // ❌ Commented delete to remove it from Actions
          // setDeleteModalVisible,
          // 'users'
        );
      },
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.tid);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...data];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        // ❌ Use sortedData directly instead of paginated data
        tableData={sortedData || []}
        isLoading={isLoading}
        exportFileName="CONTRACT"
        // ❌ Commented pagination props
        // isPagination={true}
        // recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          // setCurrentPage(0); // ❌ No need since pagination removed
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Name"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
      />

      {/* ❌ Commented out Pagination UI */}
      {/*
     <div className="pagination">
          <Pagination
            defaultCurrent={1}
            current={currentPage + 1}
            total={totalRecords}
            pageSize={pageSize} 
            onChange={(curPage, newPageSize) => {
              setCurrentPage(curPage - 1);
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize); 
                setCurrentPage(0); 
              }
            }}
            showSizeChanger 
            pageSizeOptions={["10", "20", "50", "100"]}
          />
     </div>
     */}

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "categories") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={() => {}}
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
